// Rail de herramientas. En desktop es una columna vertical a la izquierda
// (`orientation="side"`); en mobile una barra horizontal al pie del editor
// (`orientation="bottom"`), como el MobileBottomNav del shell de Atlas. Se renderiza
// dos veces desde EditorShell (una `hidden md:flex`, otra `md:hidden`), compartiendo
// active/onSelect. Cada tool abre su panel (bottom-sheet en mobile).

import type { LucideIcon } from 'lucide-react';

export interface Tool {
  id: string;
  label: string;
  icon: LucideIcon;
}

interface Props {
  tools: Tool[];
  active: string | null;
  onSelect: (id: string | null) => void;
  orientation: 'side' | 'bottom';
}

export function ToolRail({ tools, active, onSelect, orientation }: Props) {
  if (orientation === 'side') {
    return (
      <div className="hidden md:flex shrink-0 w-[68px] border-r border-neutral-200 bg-surface-card flex-col items-center py-2 gap-1 overflow-y-auto">
        {tools.map((t) => {
          const on = active === t.id;
          const Icon = t.icon;
          return (
            <button
              key={t.id}
              type="button"
              onClick={() => onSelect(on ? null : t.id)}
              className={`w-[58px] py-2 rounded-md flex flex-col items-center gap-1 text-[11px] font-medium transition active:scale-95 ${
                on ? 'bg-brand-primary/[0.08] text-brand-primary' : 'text-neutral-600 hover:bg-neutral-100'
              }`}
            >
              <Icon className="w-5 h-5" />
              <span>{t.label}</span>
            </button>
          );
        })}
      </div>
    );
  }

  // Mobile: la fila de Editar del visor —el ícono en un círculo de vidrio y el nombre
  // debajo, con scroll de costado— sobre el fondo oscuro del editor
  // (ADR-2026-10-28-edicion-canva-en-oscuro). La elegida va en blanco. Cada botón mide
  // lo que su nombre (con el círculo de mínimo) y entre todos va el MISMO aire: con un
  // ancho fijo, "Marca" y "Elementos" dejaban huecos distintos y "Dividir en capas" se cortaba.
  return (
    <div
      className="relative md:hidden shrink-0 flex gap-5 overflow-x-auto overscroll-x-contain scrollbar-hide px-4 pt-3"
      style={{ paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 12px)', touchAction: 'pan-x' }}
    >
      {tools.map((t) => {
        const on = active === t.id;
        const Icon = t.icon;
        return (
          <button
            key={t.id}
            type="button"
            onClick={() => onSelect(on ? null : t.id)}
            aria-pressed={on}
            className="flex min-w-12 shrink-0 flex-col items-center gap-1.5 transition-transform duration-150 ease-out active:scale-[0.96]"
          >
            <span
              className={`grid h-12 w-12 place-items-center rounded-full transition-colors duration-150 ${
                on ? 'bg-white text-black' : 'bg-white/[0.08] text-white/90'
              }`}
            >
              <Icon className="h-5 w-5" aria-hidden="true" />
            </span>
            <span className={`whitespace-nowrap text-center text-[12px] font-medium ${on ? 'text-white' : 'text-white/75'}`}>
              {t.label}
            </span>
          </button>
        );
      })}
    </div>
  );
}
